import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RefreshCw } from 'lucide-react';

/**
 * Session Debugger - Shows what's in localStorage for debugging auth issues
 */
export function SessionDebugger() {
  const [sessionData, setSessionData] = useState<any>({});

  const loadSessionData = () => {
    const data: any = {};
    
    // Get all pathkit-related keys from localStorage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('pathkit_')) {
        let value = localStorage.getItem(key);
        
        // Try to parse JSON if it looks like JSON
        if (value && (value.startsWith('{') || value.startsWith('['))) {
          try {
            value = JSON.parse(value);
          } catch (e) {
            // Keep as string if can't parse
          }
        }
        
        data[key] = value;
      }
    }
    
    setSessionData(data);
    console.log('[SESSION DEBUGGER] LocalStorage data:', data);
  };

  useEffect(() => {
    loadSessionData();
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Session Debugger</CardTitle>
            <CardDescription>
              Shows what's stored in localStorage for authentication
            </CardDescription>
          </div>
          <Button onClick={loadSessionData} size="sm" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {Object.keys(sessionData).length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              No session data found in localStorage
            </p>
          ) : (
            Object.entries(sessionData).map(([key, value]) => (
              <div key={key} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline">{key}</Badge>
                  {typeof value === 'object' ? (
                    <Badge>Object</Badge>
                  ) : typeof value === 'string' && value.length > 50 ? (
                    <Badge>Long String ({value.length} chars)</Badge>
                  ) : (
                    <Badge>String</Badge>
                  )}
                </div>
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-48">
                  {typeof value === 'object' 
                    ? JSON.stringify(value, null, 2)
                    : value.substring(0, 200) + (value.length > 200 ? '...' : '')}
                </pre>
              </div>
            ))
          )}
          
          <div className="pt-4 border-t">
            <h4 className="font-semibold mb-2">What to check:</h4>
            <ul className="text-sm space-y-1 text-gray-600">
              <li>• <strong>pathkit_session_id</strong> should contain a session ID like "sess_1234567890_abc123"</li>
              <li>• <strong>pathkit_user</strong> should contain user data with email and role</li>
              <li>• <strong>pathkit_company</strong> should contain company data (optional)</li>
              <li>• Old format: <strong>pathkit_access_token</strong> would contain a JWT token (deprecated)</li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
